import React from 'react';
import moment from 'moment';
import { Button, Icon, Loader, Message, Popup, Segment } from 'semantic-ui-react';

import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { PropertyWalletItemDTO } from '../../../services/properties';
import {
  ClickableTableRow,
  EmptyStateWrapper,
  LoadingWrapper,
  ResultsBadge,
  StyledTable,
  TableSectionHeader,
  TableSectionTitle,
  TableWrapper
} from '../styles';
import { propertiesWalletSectionLabel } from '../constants';
import {
  PROCESS_ICON_CONFIG,
  processEventIconStyle
} from '../utils/propertyHistoricFormatters';

type Props = {
  isLoading: boolean;
  items: PropertyWalletItemDTO[];
  currentPage: number;
  total: number;
  setCurrentPage: (page: number) => void;
  errorMessage: string | null;
  isScopeError: boolean;
  hasActiveFilters: boolean;
  listEnabled: boolean;
  onOpenDetails: (item: PropertyWalletItemDTO) => void;
};

const formatIdentification = (item: PropertyWalletItemDTO): string => {
  if (item.identification) return item.identification;
  if (item.alias) return item.alias;
  return item.publicId || '—';
};

const formatAddress = (item: PropertyWalletItemDTO): string => {
  const parts = [
    item.address?.street,
    item.address?.number,
    item.address?.complement,
    item.address?.neighborhood
  ].filter(Boolean);

  if (parts.length) return parts.join(', ');
  return item.address?.formatted || '—';
};

const formatCityState = (item: PropertyWalletItemDTO): string => {
  const city = item.address?.city;
  const state = item.address?.state;
  if (city && state) return `${city}/${state}`;
  return city || state || '—';
};

const PropertiesTable: React.FC<Props> = ({
  isLoading,
  items,
  currentPage,
  total,
  setCurrentPage,
  errorMessage,
  isScopeError,
  hasActiveFilters,
  listEnabled,
  onOpenDetails
}) => {
  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    item: PropertyWalletItemDTO
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDetails(item);
    }
  };
  const renderTableHeader = () => (
    <TableSectionHeader>
      <TableSectionTitle>{propertiesWalletSectionLabel}</TableSectionTitle>
      {!isLoading && !errorMessage && listEnabled && total > 0 && (
        <ResultsBadge>
          {total} {total === 1 ? 'imóvel' : 'imóveis'}
        </ResultsBadge>
      )}
    </TableSectionHeader>
  );

  if (!listEnabled) {
    return (
      <>
        {renderTableHeader()}
        <Segment basic>
          <Message info>
            <p style={{ margin: 0 }}>Selecione uma imobiliária para consultar a carteira de imóveis.</p>
          </Message>
        </Segment>
      </>
    );
  }

  if (errorMessage) {
    return (
      <>
        {renderTableHeader()}
        <Segment basic>
          <Message negative={!isScopeError} warning={isScopeError}>
            <p style={{ margin: 0 }}>{errorMessage}</p>
          </Message>
        </Segment>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        {renderTableHeader()}
        <LoadingWrapper>
          <Loader active inline="centered">
            Carregando carteira de imóveis…
          </Loader>
        </LoadingWrapper>
        <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
      </>
    );
  }

  if (!items.length) {
    return (
      <>
        {renderTableHeader()}
        <EmptyStateWrapper>
          <Message info>
            <p style={{ margin: 0 }}>
              {hasActiveFilters
                ? 'Nenhum imóvel encontrado com os filtros selecionados.'
                : 'Nenhum imóvel cadastrado nesta carteira. Use "Cadastrar imóvel" para adicionar o primeiro.'}
            </p>
          </Message>
        </EmptyStateWrapper>
      </>
    );
  }

  return (
    <>
      {renderTableHeader()}
      <TableWrapper>
        <StyledTable celled striped>
          <StyledTable.Header>
            <StyledTable.Row>
              <StyledTable.HeaderCell>Identificação</StyledTable.HeaderCell>
              <StyledTable.HeaderCell>Endereço</StyledTable.HeaderCell>
              <StyledTable.HeaderCell>Cidade/UF</StyledTable.HeaderCell>
              <StyledTable.HeaderCell>Tipo</StyledTable.HeaderCell>
              <StyledTable.HeaderCell>Processos</StyledTable.HeaderCell>
              <StyledTable.HeaderCell>Última atualização</StyledTable.HeaderCell>
              <StyledTable.HeaderCell textAlign="center">Ações</StyledTable.HeaderCell>
            </StyledTable.Row>
          </StyledTable.Header>
          <StyledTable.Body>
            {items.map(item => (
              <ClickableTableRow
                key={item.publicId || String(item.id)}
                tabIndex={0}
                role="button"
                onClick={() => onOpenDetails(item)}
                onKeyDown={(event: React.KeyboardEvent<HTMLTableRowElement>) =>
                  handleRowKeyDown(event, item)
                }
              >
                <StyledTable.Cell>
                  <strong>{formatIdentification(item)}</strong>
                </StyledTable.Cell>
                <StyledTable.Cell>{formatAddress(item)}</StyledTable.Cell>
                <StyledTable.Cell>{formatCityState(item)}</StyledTable.Cell>
                <StyledTable.Cell>{item.type?.description || item.type?.code || '—'}</StyledTable.Cell>
                <StyledTable.Cell
                  textAlign="center"
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                >
                  {PROCESS_ICON_CONFIG.some(
                    config => (item.activeProcesses?.[config.key] || 0) > 0
                  ) ? (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      {PROCESS_ICON_CONFIG.filter(
                        config => (item.activeProcesses?.[config.key] || 0) > 0
                      ).map(config => (
                        <Popup
                          key={config.key}
                          content={config.label}
                          trigger={
                            <span>
                              <Icon
                                name={config.icon}
                                style={processEventIconStyle}
                                aria-label={config.label}
                              />
                            </span>
                          }
                        />
                      ))}
                    </span>
                  ) : (
                    <span style={{ color: '#bbb' }}>—</span>
                  )}
                </StyledTable.Cell>
                <StyledTable.Cell>
                  {item.updatedAt
                    ? moment(item.updatedAt).format('DD/MM/YYYY HH:mm')
                    : '—'}
                </StyledTable.Cell>
                <StyledTable.Cell
                  textAlign="center"
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                >
                  <Popup
                    content="Ver detalhes"
                    trigger={
                      <Button
                        type="button"
                        icon
                        basic
                        aria-label={`Ver detalhes do imóvel ${formatIdentification(item)}`}
                        onClick={() => onOpenDetails(item)}
                      >
                        <Icon name="eye" />
                      </Button>
                    }
                  />
                </StyledTable.Cell>
              </ClickableTableRow>
            ))}
          </StyledTable.Body>
        </StyledTable>
      </TableWrapper>
      <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
    </>
  );
};

export default PropertiesTable;
